export const SW_STORE_NAME = 'sw_state';
export const SW_DB_NAME = 'reffie_sw';
export const SW_SETTING_KEY = 'notification_setting';
export const SW_MESSAGE_TYPE = 'SET_NOTIFICATION_SETTING';

export type NotificationSetting = 'all' | 'owner_only' | 'off';

export const isIndexedDbAvailable = (): boolean => {
    try {
        return typeof indexedDB !== 'undefined' && indexedDB !== null;
    } catch {
        return false;
    }
};

/**
 * Opens the IndexedDB database, creating the object store if it doesn't exist.
 */
function openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(SW_DB_NAME);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
            const db = request.result;
            if (db.objectStoreNames.contains(SW_STORE_NAME)) {
                resolve(db);
                return;
            }
            const nextVersion = db.version + 1;
            db.close();

            const upgrade = indexedDB.open(SW_DB_NAME, nextVersion);
            upgrade.onerror = () => reject(upgrade.error);
            upgrade.onupgradeneeded = () => {
                upgrade.result.createObjectStore(SW_STORE_NAME);
            };
            upgrade.onsuccess = () => resolve(upgrade.result);
        };
    });
}

function postToSw(setting: NotificationSetting | null): void {
    navigator.serviceWorker?.controller?.postMessage({
        type: SW_MESSAGE_TYPE,
        setting,
    });
}

export const saveNotificationSetting = async (setting: NotificationSetting): Promise<void> => {
    if (!isIndexedDbAvailable()) {
        postToSw(setting);
        return;
    }
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(SW_STORE_NAME, 'readwrite');
        tx.objectStore(SW_STORE_NAME).put(setting, SW_SETTING_KEY);
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => { db.close(); reject(tx.error); };
    });
};

export const clearNotificationSetting = async (): Promise<void> => {
    if (!isIndexedDbAvailable()) {
        postToSw(null);
        return;
    }
    try {
        const db = await openDb();
        return await new Promise((resolve, reject) => {
            const tx = db.transaction(SW_STORE_NAME, 'readwrite');
            tx.objectStore(SW_STORE_NAME).delete(SW_SETTING_KEY);
            tx.oncomplete = () => { db.close(); resolve(); };
            tx.onerror = () => { db.close(); reject(tx.error); };
        });
    } catch {
        // DB / store doesn't exist — nothing to clear
    }
};
